"use client";

import { Suspense, useEffect, useState, type KeyboardEvent, type MouseEvent } from "react";
import Link from "next/link";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { ThemeToggle } from "./ThemeToggle";

const LOG_PREFIX = "[PunkRecords][header]";

type HeaderProps = {
  idPrefix?: string;
  /** On /deckbuild only: Deckbuild nav resets editor to the deck list and clears ?q / ?d. */
  deckbuildSessionReset?: () => void;
};

const NAV_LINKS = [
  { id: "search", href: "/search", label: "Search" },
  { id: "advanced", href: "/advanced", label: "Advanced" },
  { id: "syntax", href: "/syntax", label: "Syntax" },
  { id: "sets", href: "/sets", label: "Sets" },
  { id: "deckbuild", href: "/deckbuild", label: "Deckbuild" },
  { id: "meta", href: "/meta", label: "Meta" },
  { id: "collection", href: "/collection", label: "Collection" },
];

const DONATE_HREF = "https://ko-fi.com/hugoprudente";

function DonateHeartIcon({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={2}
      stroke="currentColor"
      className={className}
      aria-hidden
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z"
      />
    </svg>
  );
}

function HeaderSearchField({
  base,
  idPrefix,
}: {
  base: string;
  idPrefix: string;
}) {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const qFromUrl = pathname === "/search" ? (searchParams.get("q") ?? "") : "";
  const setFromUrl =
    pathname === "/search" ? (searchParams.get("set") ?? "").trim() : "";
  const queryFromSearchPage = qFromUrl
    ? qFromUrl
    : setFromUrl
      ? `set:${setFromUrl}`
      : "";
  const [inputValue, setInputValue] = useState(queryFromSearchPage);

  useEffect(() => {
    if (pathname === "/search") {
      setInputValue(queryFromSearchPage);
    }
  }, [pathname, queryFromSearchPage]);

  const runSearchNavigation = (raw: string, source: "enter" | "submit") => {
    const trimmed = raw.trim();
    if (!trimmed) {
      // eslint-disable-next-line no-console
      console.log(LOG_PREFIX, "search skipped (empty query)", { source });
      return;
    }
    const href = `/search?q=${encodeURIComponent(trimmed)}`;
    // eslint-disable-next-line no-console
    console.log(LOG_PREFIX, "navigate to search", { source, href, query: trimmed });
    router.push(href);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      runSearchNavigation(inputValue, "enter");
    }
  };

  return (
    <form
      id={`${idPrefix}-search-form`}
      className="min-w-0 flex-1 md:max-w-md"
      onSubmit={(e) => {
        e.preventDefault();
        runSearchNavigation(inputValue, "submit");
        (e.target as HTMLFormElement).querySelector("input")?.blur();
      }}
    >
      <input
        id={`${base}-query-input`}
        type="text"
        enterKeyHint="search"
        className="w-full rounded-full border border-slate-700 bg-slate-950/80 px-4 py-2 text-sm text-slate-100 shadow-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/50"
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder="Search cards..."
      />
    </form>
  );
}

export function Header({
  idPrefix = "header",
  deckbuildSessionReset,
}: HeaderProps) {
  const base = idPrefix;
  const router = useRouter();
  const pathname = usePathname();
  const [menuOpen, setMenuOpen] = useState(false);

  const onDeckbuildChromeClick = (e: MouseEvent<HTMLAnchorElement>) => {
    if (pathname !== "/deckbuild" || !deckbuildSessionReset) return;
    e.preventDefault();
    deckbuildSessionReset();
    router.replace("/deckbuild", { scroll: false });
  };

  return (
    <header
      id={`${base}-root`}
      className="sticky top-0 z-10 border-b border-slate-800 bg-slate-950/90 backdrop-blur dark:border-slate-800 dark:bg-slate-950/90"
    >
      <div className="mx-auto flex max-w-6xl items-center justify-between gap-3 px-4 py-3">
        <div className="flex min-w-0 flex-1 items-center gap-3">
          <Link id={`${base}-logo`} href="/" className="shrink-0" title="Home">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src="/logo.svg"
              alt="Punk Records"
              className="logo-themed h-8 w-8"
            />
          </Link>
          <Suspense
            fallback={
              <div
                id={`${idPrefix}-search-form-fallback`}
                className="min-w-0 flex-1 md:max-w-md"
              >
                <div className="h-10 w-full animate-pulse rounded-full bg-slate-800/80" />
              </div>
            }
          >
            <HeaderSearchField base={base} idPrefix={idPrefix} />
          </Suspense>
        </div>

        <div className="flex shrink-0 items-center gap-2">
          <ThemeToggle />

          {/* Desktop nav */}
          <nav className="hidden items-center gap-2 text-xs md:flex">
            {NAV_LINKS.map((link) => (
              <Link
                key={link.id}
                id={`${base}-nav-${link.id}`}
                href={link.href}
                className="rounded-full bg-slate-800 px-3 py-1 text-slate-200 hover:bg-slate-700"
                onClick={
                  link.id === "deckbuild" ? onDeckbuildChromeClick : undefined
                }
              >
                {link.label}
              </Link>
            ))}
            <a
              id={`${base}-nav-donate`}
              href={DONATE_HREF}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-0.5 rounded-full bg-slate-800 px-3 py-1 text-slate-200 hover:bg-slate-700"
              title="Donate"
              aria-label="Donate on Ko-fi"
            >
              <span aria-hidden className="font-semibold">
                $
              </span>
              <DonateHeartIcon className="h-3.5 w-3.5" />
            </a>
          </nav>

          {/* Mobile hamburger */}
          <button
            id={`${base}-menu-toggle`}
            type="button"
            className="flex h-8 w-8 items-center justify-center rounded-md text-slate-300 hover:bg-slate-800 md:hidden"
            onClick={() => setMenuOpen((v) => !v)}
            aria-label="Toggle navigation"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2}
              stroke="currentColor"
              className="h-5 w-5"
            >
              {menuOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
              )}
            </svg>
          </button>
        </div>
      </div>

      {/* Mobile dropdown nav */}
      {menuOpen && (
        <nav
          id={`${base}-mobile-nav`}
          className="border-t border-slate-800 bg-slate-950/95 px-4 pb-3 pt-2 md:hidden"
        >
          <div className="flex flex-wrap gap-2">
            {NAV_LINKS.map((link) => (
              <Link
                key={link.id}
                id={`${base}-mobile-nav-${link.id}`}
                href={link.href}
                className="rounded-full bg-slate-800 px-3 py-1.5 text-xs text-slate-200 hover:bg-slate-700"
                onClick={(e) => {
                  if (link.id === "deckbuild") onDeckbuildChromeClick(e);
                  setMenuOpen(false);
                }}
              >
                {link.label}
              </Link>
            ))}
            <a
              id={`${base}-mobile-nav-donate`}
              href={DONATE_HREF}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-0.5 rounded-full bg-slate-800 px-3 py-1.5 text-xs text-slate-200 hover:bg-slate-700"
              title="Donate"
              aria-label="Donate on Ko-fi"
              onClick={() => setMenuOpen(false)}
            >
              <span aria-hidden className="font-semibold">
                $
              </span>
              <DonateHeartIcon className="h-3.5 w-3.5" />
            </a>
          </div>
        </nav>
      )}
    </header>
  );
}
